import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { PhasrRecommendationsGridWebComponent } from '../component/PhasrRecommendationsGridWebComponent';
import { PhasrRecommendationsGridView } from '../view/PhasrRecommendationsGridView';
import { templatePhasrRecommendationsGrid } from '../view/template';
import { PhasrRecommendationsGridRepository } from '../repository/PhasrRecommendationsGridRepository';
import { PhasrRecommendationsGridEvents } from '../types/grid';
import {
  PhasrRecommendationSideMenuWebComponentEvents
} from '../../../phasrSideMenus/phasrRecommendationSideMenu/types';
import {
  ActionType, ALLOW_ACTION, RESTRICT_ACTION, UNKNOWN_ACTION,
} from '../types';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { translateFn } from '../../../utils/translator.helper';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { PhasrApplyActionPanelWebComponent } from '../../../phasrApplyActionPanel';
import { Collections, PhasrApplyActionPanelEvents, PhasrApplyActionSchema } from '../../../phasrApplyActionPanel/types';
import { BehaviorProfilesOutput, BehaviorProfileStatus } from '../../phasrRestrictedProfilesSelector/types';
import { showToast } from '@nirvana/shared/common';
import { ERROR_CODE_NO_PROFILES_UPDATED } from '../../../types/common/phasrTypes';
import { isEmpty } from 'lodash';

export class PhasrRecommendationsGridController extends BaseWebComponentController {
  public component: PhasrRecommendationsGridWebComponent;
  public view: PhasrRecommendationsGridView;
  public viewConstructor: typeof PhasrRecommendationsGridView;
  public dataRepository: PhasrRecommendationsGridRepository;
  private applyRecommendationDialog: GzDialogWebComponent;

  constructor(component: PhasrRecommendationsGridWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: PhasrRecommendationsGridView,
      viewTemplate: templatePhasrRecommendationsGrid,
      dataRepositoryConstructor: PhasrRecommendationsGridRepository,
    });
  }

  public async getDataForView(): Promise<{}> {
    return {};
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$grid,
        event: PhasrRecommendationsGridEvents.OnRecommendationAllowAccess,
        callback: this.handleRecommendationAllowAccess,
      },
      {
        element: this.view.dom.$grid,
        event: PhasrRecommendationsGridEvents.OnRecommendationRestrictAccess,
        callback: this.handleRecommendationRestrictAccess,
      },
      {
        element: this.view.dom.$detailsPanel,
        event: PhasrRecommendationSideMenuWebComponentEvents.CANCEL,
        callback: this.closeDetailsSideMenu,
      },
      {
        element: this.view.dom.$detailsPanel,
        event: PhasrRecommendationSideMenuWebComponentEvents.OnRecommendationRestrictAccessFromSidePanel,
        callback: this.handleRecommendationRestrictAccess,
      },
      {
        element: this.view.dom.$detailsPanel,
        event: PhasrRecommendationSideMenuWebComponentEvents.OnRecommendationAllowAccessFromSidePanel,
        callback: this.handleRecommendationAllowAccess,
      },
    ];
  }

  private async handleRecommendationAllowAccess(event: WebComponentEvent): Promise<void> {
    const detail = event.detail;
    detail.action = ALLOW_ACTION;
    const dialogTitle = translateFn('Allow access');

    await this.openApplyRecommendationPanel(detail, dialogTitle);
  }

  private async handleRecommendationRestrictAccess(event: WebComponentEvent): Promise<void> {
    const detail = event.detail;
    detail.action = RESTRICT_ACTION;
    const dialogTitle = translateFn('Restrict access');

    await this.openApplyRecommendationPanel(detail, dialogTitle);
  }

  /**
   *
   * @param action
   * @param recommendationId
   * @param ruleId
   * @param companyId
   * @param changes
   * @private
   */
  private async updateUserBehaviouralProfiles(
    recommendationId: string,
    ruleId: number,
    companyId: string,
    action: ActionType = UNKNOWN_ACTION,
    changes: BehaviorProfilesOutput | null = null) {
    try {
      const payload = {
        recommendationId: recommendationId,
        ruleId: ruleId,
        companyId: companyId,
        action: action,
        actionName: Application.Main.getPhasrDictionary()[ruleId]?.['rule_name'] ?? 'N/A',
        changes: changes ?? null,
      };

      const response = await this.dataRepository.updateRecommendationBehaviourActions(payload);

      if (response.success) {
        showToast(this.getSuccessToastMessage(action), ToastSeverity.SUCCESS);
      } else {
        showToast(this.getErrorToastMessage(action, response.code ?? null), ToastSeverity.ERROR);
      }
    } catch (e) {
      showToast(this.getErrorToastMessage(action), ToastSeverity.ERROR);
    }
  }

  /**
   *
   * @param action
   * @private
   */
  private getSuccessToastMessage(action: ActionType): string {
    return action === ALLOW_ACTION
      ? translateFn('Successfully allowed access.')
      : translateFn('Successfully applied restriction.');
  }

  /**
   *
   * @param action
   * @param code
   * @private
   */
  private getErrorToastMessage(action: ActionType, code: number|null = null): string {
    let message = action === ALLOW_ACTION
      ? translateFn('Failed to allow access.')
      : translateFn('Failed to apply restriction.');

    switch (code) {
      case ERROR_CODE_NO_PROFILES_UPDATED:
        message = translateFn('No profiles were updated.');
        break;
      default:
        break;
    }

    return message
  }

  public getCompanyIdFilterValue(): string {
    return this.view.getCompanyIdFilterValue();
  }

  private closeDetailsSideMenu(): void {
    this.view.dom.$detailsPanel.closeSideMenu();
    this.view.dom.$detailsPanel.setMenuContent(null);
    this.view.dom.$grid.controller?.handleGridItemsLoad();
  }

  private async closeApplyRecommendationWindow(): Promise<void> {
    if (this.applyRecommendationDialog === null) {
      return;
    }

    this.applyRecommendationDialog.dismiss();
    await this.view.dom.$grid.controller?.handleGridItemsLoad();
  }

  private async openApplyRecommendationPanel(detail: PhasrApplyActionSchema, dialogTitle: any): Promise<void> {
    const applyRecommendationPanel = new PhasrApplyActionPanelWebComponent();

    const dialogConfig = {
      headline: dialogTitle,
      bodyContent: applyRecommendationPanel,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      onCloseHandler: this.closeApplyRecommendationWindow.bind(this),
    };

    this.applyRecommendationDialog = GzDialogWebComponent.buildDialogElement(dialogConfig, document.getElementById('mainPanel') as HTMLElement);
    applyRecommendationPanel.setGridData(detail);
    applyRecommendationPanel.collection = Collections.RECOMMENDATIONS;
    applyRecommendationPanel.addEventListener(
      PhasrApplyActionPanelEvents.CANCEL,
      this.closeApplyRecommendationWindow.bind(this)
    );

    applyRecommendationPanel.addEventListener(
      PhasrApplyActionPanelEvents.APPLY_ACTION,
      (applyEvent: CustomEvent) => {
        try {
          const changes = applyEvent.detail.changes;

          const createdOn = detail.createdOn ? new Date(detail.createdOn * 1000) : new Date();
          const currentDate = new Date();

          const timeDifferenceMs = currentDate.getTime() - createdOn.getTime();

          const days = Math.floor(timeDifferenceMs / (1000 * 60 * 60 * 24));
          const hours = Math.floor((timeDifferenceMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((timeDifferenceMs % (1000 * 60 * 60)) / (1000 * 60));

          const isModified = this.isRecommendationModified(changes);

          const action = detail.action ?? RESTRICT_ACTION;
          const actionName = action === ALLOW_ACTION ? 'ALLOW' : (action === RESTRICT_ACTION ? 'RESTRICT' : 'UNKNOWN');

          analytics.trackPageInteraction({
            reference: `apply-phasr-recommendation-${actionName}:${isModified ? 'MODIFIED' : 'DIRECT'}`,
            asset: `${currentDate.toISOString()}:${days}d${hours}h${minutes}m`,
          });
        } finally {
          this.updateUserBehaviouralProfiles(
            detail.id,
            detail.ruleId,
            detail.companyId,
            detail.action ?? RESTRICT_ACTION,
            applyEvent.detail.changes
          ).then().catch();
          this.closeApplyRecommendationWindow().then().catch();
        }
      },
      {once: true}
    );
    applyRecommendationPanel.injectDependencies(this.dependencies);

    await applyRecommendationPanel.start();
  }

  /**
   * Helper function to check if any item in a collection has a status other than SELECTED
   * @param collection - Array of objects to check
   * @returns True if any item has a non-SELECTED status, false otherwise
   */
  private hasNonSelectedItems(collection: { [id: string]: BehaviorProfileStatus }[]): boolean {
    return collection.some(item => Object.values(item).some(status => status !== BehaviorProfileStatus.SELECTED));
  }

  /**
   * Determines if a recommendation was modified before being applied
   * @param changes - The changes made to the recommendation
   * @returns True if the recommendation was modified, false if directly applied
   */
  private isRecommendationModified(changes: BehaviorProfilesOutput): boolean {
    return (!isEmpty(changes.departments) && this.hasNonSelectedItems(changes.departments)) ||
           (!isEmpty(changes.users) && this.hasNonSelectedItems(changes.users)) ||
           (!isEmpty(changes.profiles) && this.hasNonSelectedItems(changes.profiles));
  }
}
